/**
 * Relative-path sanitization for folder transfers (ui#94, epic ui#93) — the client half of the
 * zip-slip defense in depth. Recipients write these paths to disk when a transfer extracts as a
 * zip, so BOTH ends run the same pure rules (mirrors the api's `RelativePathSanitizer`, api#46):
 *
 * - before SENDING: registration payloads only ever carry a sanitized path (or omit it);
 * - before USING a resolve's `relativePath` (archive entry names, display): it is re-sanitized —
 *   resolve data is never trusted blindly, however well the server behaved.
 *
 * Normalization: trim → backslashes to `/` → collapse repeated `/` → strip leading `./` then `/`.
 * Rejection (→ `undefined`, treated as a flat root-level file — never an error): blank, longer
 * than {@link MAX_RELATIVE_PATH_LENGTH}, trailing `/` (a bare directory), a Windows drive prefix
 * (`C:`), control characters, or any empty/`.`/`..` segment that could escape the extraction dir.
 */

/** Upper bound on a path; matches the api's `relative_path VARCHAR(1024)` column. */
export const MAX_RELATIVE_PATH_LENGTH = 1024;

/** A Windows drive prefix such as `C:` — an absolute path we must never keep. */
const WINDOWS_DRIVE_PREFIX = /^[A-Za-z]:/;

/** ASCII control characters (< 0x20) — same range the server rejects. */
const CONTROL_CHARS = /[\u0000-\u001f]/;

/**
 * A segment made up entirely of dots and whitespace (`.`, `..`, `.. `, `...`, `   `). The bare
 * `.`/`..` are traversal; the decorated variants (trailing space/dot) can canonicalize back to
 * `..` on some extractors, so a segment that is nothing but dots and spaces is never a real name.
 */
const DOTS_AND_SPACES = /^[.\s]+$/;

/** trim → `\` to `/` → collapse repeated `/` → strip leading `./` then `/`. */
function normalize(rawPath: string): string {
	let path = rawPath.trim().replace(/\\/g, "/").replace(/\/{2,}/g, "/");
	while (path.startsWith("./")) {
		path = path.slice(2);
	}
	while (path.startsWith("/")) {
		path = path.slice(1);
	}
	return path;
}

/** Every rejection rule of the doc comment above; segments are checked post-normalization. */
function isSafe(path: string): boolean {
	if (path.length === 0 || path.length > MAX_RELATIVE_PATH_LENGTH) {
		return false;
	}
	if (path.endsWith("/") || WINDOWS_DRIVE_PREFIX.test(path) || CONTROL_CHARS.test(path)) {
		return false;
	}
	return path.split("/").every(segment => segment !== "" && !DOTS_AND_SPACES.test(segment));
}

/**
 * Normalizes and validates a client- or server-supplied relative path (forward-slash, INCLUDING
 * the leaf file name — `docs/report.pdf`). Returns the sanitized path, or `undefined` when the
 * input is absent, blank or fails any safety rule — the file is then treated as flat (root-level)
 * rather than the transfer failing.
 */
export function sanitizeRelativePath(rawPath: string | null | undefined): string | undefined {
	if (rawPath == null) {
		return undefined;
	}
	const normalized = normalize(rawPath);
	return isSafe(normalized) ? normalized : undefined;
}

/**
 * The safe basename of an UNTRUSTED file `name` for a zip entry (ui#94 review). The resolve's
 * `name` is attacker-controllable — the server sanitizes only `relativePath`, never `name` — and
 * it becomes a zip ENTRY NAME, so a `name` like `../../x` or `..\\x` would be a verbatim zip-slip.
 * Take the last path segment (after any `/` or `\\`) and refuse a degenerate one (empty, only
 * dots/spaces, or carrying a control char), falling back to a neutral placeholder that the archive
 * dedup then makes unique. A normal leaf name (no separators, real characters) passes through.
 */
function safeLeafName(name: string): string {
	const leaf = name.split(/[/\\]/).pop() ?? "";
	const trimmed = leaf.trim();
	if (trimmed === "" || DOTS_AND_SPACES.test(trimmed) || CONTROL_CHARS.test(trimmed)) {
		return "file";
	}
	return trimmed;
}

/**
 * The archive entry path for one file of a client-side zip: the sanitized `relativePath` when it
 * is present, safe AND actually ends in the file's own leaf `name`; the bare leaf name otherwise.
 * The leaf check is recipient-side defense in depth ON TOP of sanitization — resolve data whose
 * path disagrees with the file's name (e.g. a hostile `/abs` for `two.bin`) must not rename the
 * extracted file, only ever place it in folders.
 */
export function archiveNameOf(name: string, relativePath: string | null | undefined): string {
	const leaf = safeLeafName(name);
	const path = sanitizeRelativePath(relativePath);
	if (path === undefined) {
		return leaf;
	}
	// Match against the SANITIZED leaf, never the raw name — the path may only place the file in
	// folders, never rename it, and both operands are already safe.
	return path === leaf || path.endsWith(`/${leaf}`) ? path : leaf;
}

/**
 * The folder prefix a file list displays as secondary text (`docs/img/shot.png` → `docs/img`), or
 * `null` for a flat file. Derives from {@link archiveNameOf}, so what the user READS is exactly
 * where the zip will put the file — a hostile path that falls back to the leaf shows no folder.
 */
export function folderPrefixOf(name: string, relativePath: string | null | undefined): string | null {
	const archiveName = archiveNameOf(name, relativePath);
	const lastSlash = archiveName.lastIndexOf("/");
	return lastSlash === -1 ? null : archiveName.slice(0, lastSlash);
}
